/**
 * Blog single post — `/blog/:slug`.
 *
 * Renders the article body (sanitized HTML from backend) with full SEO
 * (title, meta description, canonical, JSON-LD Article schema).
 */
import React, { useCallback, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Seo, organizationJsonLd } from "@/components/Seo";
import { useI18n } from "@/contexts/I18nContext";
import { api } from "@/lib/api";
import { useImperativeLoad } from "@/hooks/useImperativeLoad";
import { BookOpen, Clock, Tag, ArrowRight, Loader2, ArrowLeft, Sparkles } from "lucide-react";

function formatDate(iso, locale) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(locale === "it" ? "it-IT" : locale === "pl" ? "pl-PL" : "en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return iso.slice(0, 10);
  }
}

export default function BlogPost() {
  const { slug } = useParams();
  const { t, locale } = useI18n();
  const [post, setPost] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setNotFound(false);
    try {
      const r = await api.get(`/blog/posts/${slug}`);
      setPost(r.data.post || null);
      setRelated(r.data.related || []);
    } catch (err) {
      if (err.response?.status === 404) {
        setNotFound(true);
      }
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useImperativeLoad(() => { load(); }, [slug]);

  const articleJsonLd = useMemo(() => {
    if (!post) return null;
    return {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: post.title,
      description: post.meta_description,
      image: post.hero_image_url ? [post.hero_image_url] : ["https://neuracom.ai/brand/og-blog.png"],
      datePublished: post.published_at,
      dateModified: post.updated_at || post.published_at,
      author: {
        "@type": "Organization",
        name: post.author_name || "NEURACOM Legal Team",
      },
      publisher: {
        "@type": "Organization",
        name: "NEURACOM by TelcaVoIP",
        logo: {
          "@type": "ImageObject",
          url: "https://neuracom.ai/brand/pwa/icon-512.png",
        },
      },
      mainEntityOfPage: {
        "@type": "WebPage",
        "@id": `https://neuracom.ai/blog/${post.slug}`,
      },
      keywords: (post.tags || []).join(", "),
      inLanguage: post.locale || "it",
    };
  }, [post]);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#0A0C1F] text-white">
        <Loader2 className="h-8 w-8 animate-spin text-[#9ACD32]" />
      </div>
    );
  }

  if (notFound || !post) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#0A0C1F] px-6 text-white">
        <div className="max-w-md text-center" data-testid="blog-post-notfound">
          <BookOpen className="mx-auto h-12 w-12 text-slate-600" />
          <h1 className="mt-4 font-display text-2xl font-bold">{t("blog.notfound_title")}</h1>
          <p className="mt-2 text-sm text-slate-400">{t("blog.notfound_body")}</p>
          <Link
            to="/blog"
            className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#9ACD32] px-5 py-2 text-sm font-bold text-[#05073B] hover:bg-[#A8DF38]"
            data-testid="blog-post-backtoindex"
          >
            <ArrowLeft className="h-4 w-4" /> {t("blog.back_to_index")}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0C1F] text-white" data-testid="blog-post-page">
      <Seo
        title={`${post.title} — NEURACOM Blog`}
        description={post.meta_description}
        canonicalPath={`/blog/${post.slug}`}
        ogImage={post.hero_image_url || "/brand/og-blog.png"}
        ogType="article"
        jsonLdList={[organizationJsonLd, articleJsonLd]}
      />

      {/* Header */}
      <header className="border-b border-slate-800 bg-gradient-to-br from-[#0B0E5C]/40 via-[#0A0C1F] to-[#05073B] px-6 py-14 lg:px-12">
        <div className="mx-auto max-w-3xl">
          <Link
            to="/blog"
            className="mb-6 inline-flex items-center gap-2 text-xs font-mono uppercase tracking-wider text-slate-400 hover:text-white"
            data-testid="blog-post-back"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> {t("blog.back_to_index")}
          </Link>
          <div className="flex flex-wrap items-center gap-2 text-[11px] font-mono uppercase tracking-wider text-slate-500">
            <span data-testid="blog-post-date">{formatDate(post.published_at, locale)}</span>
            <span>·</span>
            <span className="inline-flex items-center gap-1">
              <Clock className="h-3 w-3" /> {post.reading_time_minutes} min
            </span>
            <span>·</span>
            <span>{post.author_name}</span>
          </div>
          <h1 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-white sm:text-4xl lg:text-5xl" data-testid="blog-post-title">
            {post.title}
          </h1>
          {post.meta_description && (
            <p className="mt-4 max-w-2xl text-base leading-relaxed text-slate-300 sm:text-lg">
              {post.meta_description}
            </p>
          )}
          {post.tags?.length > 0 && (
            <div className="mt-6 flex flex-wrap gap-2" data-testid="blog-post-tags">
              {post.tags.map((tag) => (
                <Link
                  key={tag}
                  to={`/blog?tag=${encodeURIComponent(tag)}`}
                  className="inline-flex items-center gap-1 rounded-full border border-slate-700 bg-slate-900/60 px-3 py-1 text-[11px] font-semibold text-slate-300 transition-colors hover:border-[#9ACD32]/40 hover:text-[#9ACD32]"
                >
                  <Tag className="h-2.5 w-2.5" /> {tag}
                </Link>
              ))}
            </div>
          )}
        </div>
      </header>

      {/* Body */}
      <article className="mx-auto max-w-3xl px-6 py-12 lg:px-0">
        {post.hero_image_url && (
          <img
            src={post.hero_image_url}
            alt={post.title}
            loading="eager"
            decoding="async"
            className="mb-10 aspect-[16/9] w-full rounded-2xl object-cover"
          />
        )}
        <div
          className="prose prose-invert prose-headings:font-display prose-headings:text-white prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-3 prose-h3:text-xl prose-h3:mt-6 prose-h3:mb-2 prose-p:text-slate-300 prose-p:leading-relaxed prose-strong:text-white prose-a:text-[#9ACD32] prose-a:no-underline hover:prose-a:underline prose-code:bg-slate-800 prose-code:px-1 prose-code:py-0.5 prose-code:rounded prose-code:text-[#9ACD32] prose-blockquote:border-l-[#9ACD32] prose-blockquote:text-slate-300 prose-ul:text-slate-300 prose-ol:text-slate-300 prose-li:my-1 max-w-none"
          data-testid="blog-post-body"
          dangerouslySetInnerHTML={{ __html: post.body_html }}
        />

        {/* Product CTA */}
        <Card className="mt-16 border-[#9ACD32]/30 bg-gradient-to-br from-[#0B0E5C]/50 to-[#05073B] p-6 lg:p-8" data-testid="blog-post-cta">
          <div className="flex items-start gap-4">
            <div className="hidden sm:flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-[#9ACD32]/15 text-[#9ACD32]">
              <Sparkles className="h-6 w-6" />
            </div>
            <div className="flex-1">
              <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#9ACD32]">{t("blog.cta_pretitle")}</p>
              <h3 className="mt-2 font-display text-xl font-bold text-white">{t("blog.cta_title")}</h3>
              <p className="mt-2 text-sm text-slate-300">{t("blog.cta_body")}</p>
              <div className="mt-4 flex flex-wrap gap-3">
                <Link
                  to="/pricing"
                  className="inline-flex items-center gap-2 rounded-full bg-[#9ACD32] px-5 py-2 text-sm font-bold text-[#05073B] hover:bg-[#A8DF38]"
                  data-testid="blog-post-cta-pricing"
                >
                  {t("blog.cta_button_pricing")} <ArrowRight className="h-3.5 w-3.5" />
                </Link>
                <Link
                  to="/legal/jurisprudence"
                  className="inline-flex items-center gap-2 rounded-full border border-slate-700 bg-slate-900/60 px-5 py-2 text-sm font-bold text-white hover:border-[#9ACD32]/50"
                  data-testid="blog-post-cta-jurisprudence"
                >
                  {t("blog.cta_button_jurisprudence")}
                </Link>
              </div>
            </div>
          </div>
        </Card>
      </article>

      {/* Related */}
      {related.length > 0 && (
        <section className="border-t border-slate-800 bg-[#05073B]/40 px-6 py-12 lg:px-12" data-testid="blog-related">
          <div className="mx-auto max-w-6xl">
            <h2 className="mb-6 font-display text-2xl font-bold text-white">{t("blog.related_title")}</h2>
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {related.map((r) => (
                <Link
                  key={r.post_id}
                  to={`/blog/${r.slug}`}
                  className="block"
                  data-testid={`blog-related-${r.slug}`}
                >
                  <Card className="h-full border-slate-800 bg-slate-900/60 p-5 transition-all hover:-translate-y-1 hover:border-[#9ACD32]/40">
                    <div className="mb-2 flex items-center gap-2 text-[10px] font-mono uppercase tracking-wider text-slate-500">
                      <span>{formatDate(r.published_at, locale)}</span>
                      <span>·</span>
                      <span>{r.reading_time_minutes} min</span>
                    </div>
                    <h3 className="line-clamp-2 font-display text-base font-bold text-white">
                      {r.title}
                    </h3>
                    <p className="mt-2 line-clamp-3 text-sm text-slate-400">
                      {r.meta_description}
                    </p>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
